/**
 * Forgot Password Page Component
 * Hope SACCO Frontend
 * ✅ Sends Firebase password reset email with correct redirect to /reset-password
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { sendPasswordResetEmail } from 'firebase/auth';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { auth, db } from '../config/firebase';
import './ForgotPasswordPage.css';

const ForgotPasswordPage = () => {
  const navigate = useNavigate();

  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!email.trim()) {
      setError('Email is required');
      return;
    }

    if (!validateEmail(email)) {
      setError('Invalid email format');
      return;
    }

    setIsLoading(true);

    try {
      // Check if email exists in users collection first
      const q = query(collection(db, 'users'), where('emailLower', '==', email.trim().toLowerCase()));
      const snap = await getDocs(q);

      if (snap.empty) {
        setError('This email is not registered with Hope SACCO. Please speak to the Hope SACCO admin to register you first.');
        setIsLoading(false);
        return;
      }

      await sendPasswordResetEmail(auth, email.trim(), {
        // ✅ Points to our ResetPasswordPage which handles the oobCode
        url: 'https://saccoteck.com/reset-password',
        // ✅ Must be true so the link opens inside our React app
        handleCodeInApp: true,
      });

      console.log('✅ Password reset email sent to:', email);
      setIsSuccess(true);

    } catch (error) {
      console.error('❌ Password reset error:', error.code, error.message);

      if (error.code === 'auth/user-not-found') {
        setError('This email is not registered with Hope SACCO. Please speak to the Hope SACCO admin to register you first.');
      } else if (error.code === 'auth/invalid-email') {
        setError('Invalid email address');
      } else if (error.code === 'auth/too-many-requests') {
        setError('Too many requests. Please try again later');
      } else if (error.code === 'auth/unauthorized-continue-uri') {
        setError('System configuration error. Please contact support.');
        console.error('⚠️ Add saccoteck.com to Firebase Console → Authentication → Settings → Authorized domains');
      } else {
        setIsSuccess(true); // Show success for security on unknown errors
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleBackToLogin = () => {
    navigate('/login');
  };

  if (isSuccess) {
    return (
      <div className="forgot-password-page">
        <div className="forgot-password-container">
          <div className="forgot-password-box">
            <div className="success-icon">✅</div>
            <h1>Check Your Email</h1>
            <p className="success-message">
              If an account exists with <strong>{email}</strong>, you will receive a password reset link shortly.
            </p>
            <p className="help-text">
              Please check your inbox and spam folder. The link will expire in 1 hour.
            </p>
            <button
              onClick={handleBackToLogin}
              className="btn btn-primary btn-full"
            >
              Back to Login
            </button>
            <div className="resend-section">
              <p>Didn't receive the email?</p>
              <button
                onClick={() => setIsSuccess(false)}
                className="link-button"
              >
                Try again
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="forgot-password-page">
      <div className="forgot-password-container">
        <div className="forgot-password-box">
          {/* Header */}
          <div className="forgot-password-header">
            <div className="icon">🔒</div>
            <h1>Forgot Password?</h1>
            <p>No worries! Enter your email address and we'll send you a link to reset your password.</p>
          </div>

          {/* Error Alert */}
          {error && (
            <div className="alert alert-error">
              <span className="alert-icon">⚠️</span>
              <span>{error}</span>
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="forgot-password-form" noValidate>
            <div className="form-group">
              <label htmlFor="email">
                Email Address <span className="required">*</span>
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={email}
                onChange={(e) => { setEmail(e.target.value); setError(''); }}
                className={error ? 'input-error' : ''}
                placeholder="Enter your email"
                disabled={isLoading}
                autoComplete="email"
                autoFocus
              />
              {error && <span className="error-message">{error}</span>}
            </div>

            <button
              type="submit"
              className="btn btn-primary btn-full"
              disabled={isLoading}
            >
              {isLoading ? (
                <><span className="spinner"></span>Sending...</>
              ) : (
                'Send Reset Link'
              )}
            </button>
          </form>

          {/* Back to Login */}
          <div className="back-to-login">
            <button
              onClick={handleBackToLogin}
              className="link-button"
              disabled={isLoading}
            >
              ← Back to Login
            </button>
          </div>

          {/* Support Info */}
          <div className="support-info">
            <p><strong>Having trouble?</strong></p>
            <p>Contact support at <a href="mailto:support@saccoteck.com">support@saccoteck.com</a></p>
            <p>or call <a href="tel:+254700000000">+254 700 000 000</a></p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;